import React, { useState, useMemo } from 'react';
import { marked } from 'marked';
import { jsonToHtml, containsUrls, getDataPreview, isStringArray } from '../../utils/jsonToHtml';
import FeedbackLoopResultsDisplay from './FeedbackLoopResultsDisplay';
import Tooltip from '../common/Tooltip';

/** Copy text to clipboard with fallback for non-secure contexts. */
async function clipboardCopy(text: string): Promise<void> {
  try {
    await navigator.clipboard.writeText(text);
  } catch {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand('copy');
    document.body.removeChild(textarea);
  }
}

interface EnhancedFormattedDisplayProps {
  data: any;
  title?: string;
  showToggle?: boolean;
  maxHeight?: string;
  className?: string;
  initiallyExpanded?: boolean;
  displayType?: 'input' | 'output';
  workflowSteps?: Array<{ orchestration_step_id: string | number; step_type: string; description?: string }>;
}

function objectWithNumericKeysToArray(obj: any): string[] | null {
  if (
    obj &&
    typeof obj === 'object' &&
    !Array.isArray(obj) &&
    Object.keys(obj).length > 0 &&
    Object.keys(obj).every(k => !isNaN(Number(k)))
  ) {
    return Object.keys(obj)
      .sort((a, b) => Number(a) - Number(b))
      .map(k => obj[k]);
  }
  return null;
}

const EnhancedFormattedDisplay: React.FC<EnhancedFormattedDisplayProps> = ({
  data,
  title,
  showToggle = true,
  maxHeight = 'max-h-96',
  className = '',
  initiallyExpanded = false,
  displayType = 'output',
  workflowSteps = [],
}) => {
  const [showFormatted, setShowFormatted] = useState(true);
  const [isExpanded, setIsExpanded] = useState(initiallyExpanded);

  // Check if data contains URLs
  const hasUrls = useMemo(() => containsUrls(data), [data]);

  // Get data preview
  const dataPreview = useMemo(() => getDataPreview(data), [data]);

  // Check if this is a feedback loop result
  const isFeedbackLoopResult = useMemo(() => {
    return (
      typeof data === 'object' &&
      data !== null &&
      !Array.isArray(data) &&
      data.action === 'feedback_loop' &&
      typeof data.feedback === 'string' &&
      typeof data.max_attempts === 'number' &&
      typeof data.next_step_id === 'string' &&
      typeof data.iteration_number === 'number' &&
      typeof data.loop_iteration_id === 'number'
    );
  }, [data]);

  // Check if this is fan-out results (array of strings or objects with content)
  const isFanOutResults = useMemo(() => {
    // Don't treat feedback loop results as fan-out results
    if (isFeedbackLoopResult) {
      return false;
    }
    
    let arrayData = data;
    if (!Array.isArray(data)) {
      const arr = objectWithNumericKeysToArray(data);
      if (arr) arrayData = arr;
    }
    
    // Check if it's an array of strings
    if (isStringArray(arrayData)) {
      return true;
    }
    
    // Check if it's an array of objects with content property (from fan-out input)
    if (Array.isArray(arrayData) && arrayData.length > 0) {
      return arrayData.every(item => 
        typeof item === 'object' && 
        item !== null && 
        'content' in item && 
        typeof item.content === 'string'
      );
    }
    
    // Check if it's a single string or any array (to apply fan-out styling)
    if (typeof data === 'string' || (Array.isArray(data) && data.length > 0)) {
      return true;
    }
    
    return false;
  }, [data, isFeedbackLoopResult]);

  // Function to convert URLs to clickable links
  const convertUrlsToLinks = (text: string): string => {
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    return text.replace(urlRegex, (url) => {
      return `<a href="${url}" target="_blank" rel="noopener noreferrer" class="text-primary-600 hover:text-primary-800 underline">${url}</a>`;
    });
  };

  // Function to process content with both markdown and link conversion
  const processContent = (content: string): string => {
    // First convert URLs to links
    const withLinks = convertUrlsToLinks(content);
    // Then parse markdown if available
    try {
      return marked.parse(withLinks) as string;
    } catch (error) {
      // Fallback to just the content with links if markdown parsing fails
      return withLinks;
    }
  };

  // Helper function to escape HTML (for non-string items in arrays)
  const escapeHtml = (text: string): string => {
    if (typeof document !== 'undefined') {
      const div = document.createElement('div');
      div.textContent = text;
      return div.innerHTML;
    }
    // Fallback for Node.js environment (testing)
    return text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  };

  // Enhanced display for string arrays or object arrays with content (like fan-out results)
  const renderStringArrayDisplay = (items: any[], itemTitle: string) => {
    return (
      <div className="space-y-4">
        {items.map((item, idx) => {
          // Extract content from item (could be string or object with content property)
          const content = typeof item === 'string' ? item : item.content || '';
          const role = typeof item === 'object' && item.role ? item.role : '';
          
          return (
            <div
              key={idx}
              className={`relative border rounded-lg shadow-sm p-4 transition bg-white dark:bg-cap-dark ${idx % 2 === 0 ? 'bg-gray-50 dark:bg-gray-900' : 'bg-white dark:bg-cap-dark'}`}
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center space-x-2">
                  <span className="font-bold text-blue-700 dark:text-blue-300">{itemTitle} {idx + 1}</span>
                  {role && (
                    <span className="text-xs px-2 py-1 bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 rounded-full">
                      {role}
                    </span>
                  )}
                </div>
                <Tooltip content={`Copy this ${itemTitle.toLowerCase()}`}>
                <button
                  className="text-xs text-gray-500 dark:text-gray-400 hover:underline"
                  onClick={() => clipboardCopy(content)}
                >
                  Copy
                </button>
                </Tooltip>
              </div>
              <div
                className="prose prose-sm dark:prose-invert max-w-none"
                dangerouslySetInnerHTML={{ __html: processContent(content) }}
              />
            </div>
          );
        })}
      </div>
    );
  };

  // Enhanced display for object with multiple key-value pairs
  const renderObjectDisplay = (obj: any) => {
    const renderNestedValue = (value: any, depth: number = 0): React.ReactNode => {
      if (typeof value === 'string') {
        // Check if the string looks like markdown (contains markdown syntax)
        const isMarkdown = value.includes('#') || value.includes('**') || value.includes('*') || value.includes('```') || value.includes('[');
        
        if (isMarkdown) {
          return (
            <div
              className="prose prose-sm dark:prose-invert max-w-none"
              style={{ minHeight: '3rem' }}
              dangerouslySetInnerHTML={{ __html: processContent(value) }}
            />
          );
        } else {
          // For plain text, just escape and convert URLs
          const escapedValue = escapeHtml(value);
          const withLinks = convertUrlsToLinks(escapedValue);
          return (
            <div className="whitespace-pre-wrap text-sm">
              <div dangerouslySetInnerHTML={{ __html: withLinks }} />
            </div>
          );
        }
      } else if (Array.isArray(value)) {
        return (
          <div className="space-y-2">
            {value.map((item, idx) => (
              <div key={idx} className={`border-l-2 border-gray-200 dark:border-gray-700 pl-3 ${depth > 0 ? 'ml-4' : ''}`}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-medium text-gray-600 dark:text-gray-400">[{idx}]:</span>
                  <Tooltip content="Copy this item">
                  <button
                    className="text-xs text-gray-500 dark:text-gray-400 hover:underline"
                    onClick={() => clipboardCopy(typeof item === 'string' ? item : JSON.stringify(item, null, 2))}
                  >
                    Copy
                  </button>
                  </Tooltip>
                </div>
                {renderNestedValue(item, depth + 1)}
              </div>
            ))}
          </div>
        );
      } else if (typeof value === 'object' && value !== null) {
        return (
          <div className="space-y-2">
            {Object.entries(value).map(([nestedKey, nestedValue]) => (
              <div key={nestedKey} className={`border-l-2 border-gray-200 dark:border-gray-700 pl-3 ${depth > 0 ? 'ml-4' : ''}`}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{nestedKey}:</span>
                  <Tooltip content="Copy this value">
                  <button
                    className="text-xs text-gray-500 dark:text-gray-400 hover:underline"
                    onClick={() => clipboardCopy(typeof nestedValue === 'string' ? nestedValue : JSON.stringify(nestedValue, null, 2))}
                  >
                    Copy
                  </button>
                  </Tooltip>
                </div>
                {renderNestedValue(nestedValue, depth + 1)}
              </div>
            ))}
          </div>
        );
      } else {
        return (
          <div className="text-sm font-mono bg-gray-50 dark:bg-gray-900 p-2 rounded">
            {JSON.stringify(value, null, 2)}
          </div>
        );
      }
    };

    return (
      <div className="space-y-4">
        {Object.entries(obj).map(([key, value], idx) => (
          <div
            key={key}
            className={`relative border rounded-lg shadow-sm p-4 transition bg-white dark:bg-cap-dark ${idx % 2 === 0 ? 'bg-gray-50 dark:bg-gray-900' : 'bg-white dark:bg-cap-dark'}`}
          >
            <div className="flex items-center justify-between mb-2">
              <span className="font-bold text-blue-700 dark:text-blue-300">{key}</span>
              <Tooltip content="Copy this value">
              <button
                className="text-xs text-gray-500 dark:text-gray-400 hover:underline"
                onClick={() => clipboardCopy(typeof value === 'string' ? value : JSON.stringify(value, null, 2))}
              >
                Copy
              </button>
              </Tooltip>
            </div>
            <div>
              {renderNestedValue(value)}
            </div>
          </div>
        ))}
      </div>
    );
  };

  // Convert data to formatted HTML or React node
  const formattedContent = useMemo(() => {
    if (!showFormatted) {
      // Return raw JSON string
      const rawData = typeof data === 'string' ? data : JSON.stringify(data, null, 2);
      return `<pre class="font-mono text-sm whitespace-pre-wrap">${rawData}</pre>`;
    }

    // Convert numeric-keyed object to array if needed
    let arrayData = data;
    if (!Array.isArray(data)) {
      const arr = objectWithNumericKeysToArray(data);
      if (arr) arrayData = arr;
    }

    // Special case: feedback loop result
    if (isFeedbackLoopResult) {
      return <FeedbackLoopResultsDisplay data={data} workflowSteps={workflowSteps} />;
    }

    // Special case: array of strings or objects with content (fan_out_results)
    if (isStringArray(arrayData) || (Array.isArray(arrayData) && arrayData.length > 0 && arrayData.every(item => 
      typeof item === 'object' && 
      item !== null && 
      'content' in item && 
      typeof item.content === 'string'
    ))) {
      return renderStringArrayDisplay(arrayData, displayType === 'input' ? 'Input Item' : 'Result');
    }

    // Special case: single string - render directly (outer header already has title + Copy)
    if (typeof data === 'string') {
      return (
        <div
          className="prose prose-sm dark:prose-invert max-w-none"
          style={{ minHeight: '3rem' }}
          dangerouslySetInnerHTML={{ __html: processContent(data) }}
        />
      );
    }

    // Special case: array of any type - format each item like a fan-out result
    if (Array.isArray(data) && data.length > 0) {
      return (
        <div className="space-y-4">
          {data.map((item, idx) => {
            const itemContent = typeof item === 'string' ? item : JSON.stringify(item, null, 2);
            return (
              <div
                key={idx}
                className={`relative border rounded-lg shadow-sm p-4 transition bg-white dark:bg-cap-dark ${idx % 2 === 0 ? 'bg-gray-50 dark:bg-gray-900' : 'bg-white dark:bg-cap-dark'}`}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center space-x-2">
                    <span className="font-bold text-blue-700 dark:text-blue-300">Item {idx + 1}</span>
                    <span className="text-xs px-2 py-1 bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 rounded-full">
                      {typeof item}
                    </span>
                  </div>
                  <Tooltip content="Copy this item">
                  <button
                    className="text-xs text-gray-500 dark:text-gray-400 hover:underline"
                    onClick={() => clipboardCopy(itemContent)}
                  >
                    Copy
                  </button>
                  </Tooltip>
                </div>
                <div
                  className="prose prose-sm dark:prose-invert max-w-none"
                  dangerouslySetInnerHTML={{ __html: typeof item === 'string' ? processContent(item) : `<pre class="text-sm">${escapeHtml(itemContent)}</pre>` }}
                />
              </div>
            );
          })}
        </div>
      );
    }

    // Special case: object with multiple key-value pairs
    if (typeof data === 'object' && data !== null && !Array.isArray(data) && Object.keys(data).length > 1) {
      return renderObjectDisplay(data);
    }

    // Special case: single object with one key-value pair - also use object display for consistency
    if (typeof data === 'object' && data !== null && !Array.isArray(data) && Object.keys(data).length === 1) {
      return renderObjectDisplay(data);
    }

    // Default case: use existing jsonToHtml
    return jsonToHtml(data, title);
  }, [data, title, showFormatted, displayType, isFeedbackLoopResult, workflowSteps]);

  // Get raw JSON string for copy functionality
  const rawJsonString = useMemo(() => {
    return typeof data === 'string' ? data : JSON.stringify(data, null, 2);
  }, [data]);

  const [copyLabel, setCopyLabel] = useState('Copy');

  const copyToClipboard = async () => {
    await clipboardCopy(rawJsonString);
    setCopyLabel('Copied!');
    setTimeout(() => setCopyLabel('Copy'), 2000);
  };

  const toggleView = () => {
    setShowFormatted(!showFormatted);
  };

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  return (
    <div className={`bg-white dark:bg-cap-dark border border-gray-200 dark:border-gray-700 rounded-lg ${className}`}>
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-2 p-3 sm:p-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center space-x-2 sm:space-x-3 min-w-0">
          <div className="flex items-center min-w-0">
            <div className={`w-5 h-5 sm:w-6 sm:h-6 rounded-full flex items-center justify-center mr-1.5 sm:mr-2 flex-shrink-0 ${
              displayType === 'input' ? 'bg-green-100 dark:bg-green-900/30' : 'bg-blue-100 dark:bg-blue-900/30'
            }`}>
              <svg className={`w-3.5 h-3.5 sm:w-4 sm:h-4 ${displayType === 'input' ? 'text-green-600' : 'text-primary-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {displayType === 'input' ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16l-4-4m0 0l4-4m-4 4h18" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                )}
              </svg>
            </div>
            <h3 className="text-sm sm:text-lg font-semibold text-gray-900 dark:text-warm-white truncate">
              {title || (displayType === 'input' ? 'Input Data' : 'Output Data')}
            </h3>
            {title && (
              <div className="text-xs text-gray-500 dark:text-gray-400 ml-2 hidden sm:block">
                {typeof data === 'object' && data !== null && !Array.isArray(data)
                  ? `${Object.keys(data).length} key${Object.keys(data).length !== 1 ? 's' : ''}`
                  : Array.isArray(data)
                    ? `${data.length} item${data.length !== 1 ? 's' : ''}`
                    : typeof data
                }
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center space-x-1.5 sm:space-x-2">
          {showToggle && (
            <button
              onClick={toggleView}
              className="text-xs px-1.5 sm:px-2 py-1 rounded border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 dark:bg-gray-900 transition-colors"
            >
              {showFormatted ? 'Raw' : 'Formatted'}
            </button>
          )}

          <Tooltip content="Copy to clipboard">
          <button
            onClick={copyToClipboard}
            className="text-xs px-1.5 sm:px-2 py-1 rounded border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 dark:bg-gray-900 transition-colors"
          >
            {copyLabel}
          </button>
          </Tooltip>

          <button
            onClick={toggleExpanded}
            className="text-xs px-1.5 sm:px-2 py-1 rounded border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 dark:bg-gray-900 transition-colors"
          >
            {isExpanded ? 'Collapse' : 'Expand'}
          </button>
        </div>
      </div>

      {/* Preview when collapsed */}
      {!isExpanded && (
        <div className="p-3 sm:p-4">
          <div className="text-sm text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-900 p-2 sm:p-3 rounded border">
            <div className="flex items-center justify-between mb-2">
              <span className="font-medium">Preview:</span>
              <span className="text-xs text-gray-500 dark:text-gray-400">{typeof data}</span>
            </div>
            <div className="text-gray-700 dark:text-gray-300">{dataPreview}</div>
          </div>
        </div>
      )}

      {/* Expanded content */}
      {isExpanded && (
        <div className={`bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg overflow-y-auto overflow-x-auto w-full ${isFanOutResults ? '' : maxHeight} min-h-0`}>
          {typeof formattedContent === 'string' ? (
            <div
              className="p-3 sm:p-4 text-sm leading-relaxed w-full"
              dangerouslySetInnerHTML={{ __html: formattedContent }}
            />
          ) : (
            <div className="p-3 sm:p-4 text-sm leading-relaxed w-full">{formattedContent}</div>
          )}
        </div>
      )}

      {/* Footer with additional info */}
      {isExpanded && (
        <div className="text-xs text-gray-500 dark:text-gray-400 flex flex-wrap items-center justify-between gap-2 p-3 sm:p-4 border-t border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-3 sm:space-x-4">
            <span>Type: {typeof data}</span>
            {Array.isArray(data) && <span>Items: {data.length}</span>}
            {typeof data === 'object' && data !== null && !Array.isArray(data) && (
              <span>Keys: {Object.keys(data).length}</span>
            )}
          </div>
          {hasUrls && (
            <span className="text-primary-600">
              💡 Links are clickable and open in new tabs
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default EnhancedFormattedDisplay; 